import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import backendApi from "../../config/backendApi";
import BrokenRefReport, { IBrokenRefReport } from "../../components/BrokenRefReport";

import "./Fix.css";

function Fix() {
    const { uuId } = useParams();
    const [diff, setDiff] = useState<string>("");
    const [page, setPage] = useState<string>("");
    const [language, setLanguage] = useState<string>("");
    const [report, setReport] = useState<IBrokenRefReport | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [summary, setSummary] = useState<string>(
        "Fixed citation errors using [[User:CiteFix|CiteFix]]"
    );
    const [editing, setEditing] = useState<boolean>(false);
    const [editResult, setEditResult] = useState<{ success: boolean; message: string } | null>(null);

    useEffect(() => {
        backendApi
            .post("/diff/" + uuId)
            .then(({ data }) => {
                setDiff(data.diff);
                setPage(data.page);
                setLanguage(data.language);
                setReport(data.report || null);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Failed to load diff");
                setLoading(false);
            });
    }, [uuId]);

    const handleEdit = () => {
        setEditing(true);
        setEditResult(null);
        backendApi
            .post("/edit", { uuid: uuId, summary })
            .then(({ data }) => {
                setEditResult({ success: true, message: data.message });
                setEditing(false);
            })
            .catch((err) => {
                setEditResult({
                    success: false,
                    message: err.response?.data?.message || "Edit failed",
                });
                setEditing(false);
            });
    };

    if (loading) {
        return (
            <Container fixed>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: "120px" }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fixed>
                <Alert severity="error" sx={{ marginTop: "80px" }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container fixed>
            <Paper elevation={6} sx={{ marginTop: "80px", padding: "30px" }}>
                <Typography variant="h5" gutterBottom>
                    Diff preview: {page}
                </Typography>

                <BrokenRefReport report={report} />

                {diff ? (
                    <Box sx={{ overflowX: "auto", marginTop: "16px" }}>
                        <table className="diff">
                            <tbody
                                dangerouslySetInnerHTML={{ __html: diff }}
                            />
                        </table>
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ marginTop: "16px" }}>
                        No changes detected.
                    </Alert>
                )}

                <Box sx={{ marginTop: "24px" }}>
                    <TextField
                        label="Edit summary"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        fullWidth
                        variant="outlined"
                    />
                </Box>

                <Box sx={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "16px" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEdit}
                        disabled={editing || !diff}
                    >
                        {editing ? "Submitting..." : "Edit page"}
                    </Button>
                    {editing && <CircularProgress size={24} />}
                </Box>

                {editResult && (
                    <Alert
                        severity={editResult.success ? "success" : "error"}
                        sx={{ marginTop: "16px" }}
                    >
                        {editResult.message}
                        {editResult.success && (
                            <>
                                {" — "}
                                <a
                                    href={`https://${language}.wikipedia.org/wiki/${encodeURIComponent(page)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    View article
                                </a>
                            </>
                        )}
                    </Alert>
                )}
            </Paper>
        </Container>
    );
}

export default Fix;
